import React, { useEffect, useRef, useState } from "react";
import { addDoc, collection } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, auth, storage } from "../firebase";
import { useNavigate } from "react-router-dom";
import {
  FaPills,
  FaCalendarAlt,
  FaBoxOpen,
  FaInfoCircle,
  FaPlus,
  FaCamera,
  FaUpload,
  FaTrash,
} from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./MedicationForm.css";

const AddMedication = () => {
  const navigate = useNavigate();

  // Refs
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Estado del formulario
  const initialForm = {
    name: "",
    dosage: "",
    expirationDate: "",
    quantity: "",
    condition: "",
    category: "",
    description: "",
    imageFile: null,
    imagePreview: null,
  };

  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Opciones de selects
  const categories = [
    "Analgésico",
    "Antibiótico",
    "Antihistamínico",
    "Antiinflamatorio",
    "Antipirético",
    "Antidepresivo",
    "Ansiolítico",
    "Anticonvulsivo",
    "Antidiabético",
    "Antihipertensivo",
    "Anticoagulante",
    "Antiemético",
    "Antiacido",
    "Laxante",
    "Antidiarreico",
    "Vitaminas y Suplementos",
    "Antimicótico",
    "Antiviral",
    "Hormonas",
    "Broncodilatador",
    "Antipsicótico",
    "Antiparasitario",
    "Antiasmático",
    "Anticonceptivo",
    "Medicamentos para el corazón",
    "Medicamentos para la tiroides",
    "Medicamentos para el colesterol",
    "Medicamentos para la osteoporosis",
  ];

  const conditions = ["Nuevo", "Abierto", "Sin abrir"];

  /* --------------------------- Utilidades --------------------------- */
  const todayStr = () => new Date().toISOString().split("T")[0];

  const stopCamera = () => {
    try {
      const stream = videoRef.current?.srcObject;
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
        if (videoRef.current) videoRef.current.srcObject = null;
      }
    } catch {}
    setCameraActive(false);
  };

  const resetForm = () => {
    // Limpia TODO (incluye imagen y file input)
    setFormData({ ...initialForm });
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = "";
    stopCamera();
  };

  useEffect(() => {
    // Limpieza al desmontar componente
    return () => stopCamera();
  }, []);

  /* --------------------------- Manejadores --------------------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        imageFile: file,
        imagePreview: reader.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraActive(true);
    } catch (err) {
      console.error("Error al acceder a la cámara:", err);
      alert(
        "No se pudo acceder a la cámara. Asegúrate de dar los permisos necesarios."
      );
    }
  };

  const takePhoto = () => {
    if (!canvasRef.current || !videoRef.current) return;
    const context = canvasRef.current.getContext("2d");
    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;
    context.drawImage(
      videoRef.current,
      0,
      0,
      canvasRef.current.width,
      canvasRef.current.height
    );
    canvasRef.current.toBlob(
      (blob) => {
        if (!blob) return;
        const file = new File([blob], "medication-photo.jpg", {
          type: "image/jpeg",
        });
        setFormData((prev) => ({
          ...prev,
          imageFile: file,
          imagePreview: canvasRef.current?.toDataURL("image/jpeg") || null,
        }));
        stopCamera();
      },
      "image/jpeg",
      0.95
    );
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, imageFile: null, imagePreview: null }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /* --------------------------- Validación --------------------------- */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "El nombre es requerido";
    if (!formData.dosage.trim()) newErrors.dosage = "La dosis es requerida";

    if (!formData.expirationDate)
      newErrors.expirationDate = "La fecha de vencimiento es requerida";

    if (formData.expirationDate) {
      // Comparación por fecha (sin horas) para evitar falsos negativos por TZ
      const today = new Date();
      const todayOnly = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );
      const exp = new Date(formData.expirationDate + "T00:00:00");
      if (exp < todayOnly) newErrors.expirationDate = "La fecha no puede ser pasada";
    }

    if (!formData.quantity) newErrors.quantity = "La cantidad es requerida";
    const qty = Number(formData.quantity);
    if (Number.isNaN(qty)) newErrors.quantity = "Debe ser un número";
    if (!Number.isNaN(qty) && qty <= 0) newErrors.quantity = "Debe ser mayor a 0";

    if (!formData.condition) newErrors.condition = "La condición es requerida";
    if (!formData.category) newErrors.category = "La categoría es requerida";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* --------------------------- Submit --------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      if (!auth.currentUser) {
        throw new Error("Debes iniciar sesión para agregar medicamentos");
      }

      let imageUrl = "";

      // Subir imagen si existe
      if (formData.imageFile) {
        const storageRef = ref(
          storage,
          `medications/${auth.currentUser.uid}/${Date.now()}_${formData.imageFile.name}`
        );
        await uploadBytes(storageRef, formData.imageFile);
        imageUrl = await getDownloadURL(storageRef);
      }

      const medicationData = {
        name: formData.name.trim(),
        dosage: formData.dosage.trim(),
        expirationDate: formData.expirationDate,
        quantity: Number(formData.quantity),
        condition: formData.condition,
        category: formData.category,
        description: formData.description?.trim() || "",
        donorId: auth.currentUser.uid,
        donorEmail: auth.currentUser.email,
        createdAt: new Date(),
        updatedAt: new Date(),
        imageUrl: imageUrl || null,
      };

      await addDoc(collection(db, "medications"), medicationData);

      // ✅ Limpieza total tras alta
      resetForm();

      // Mostrar banner con Bootstrap Icon en lugar de window.confirm
      setShowSuccess(true);
    } catch (error) {
      console.error("Error al agregar medicamento:", error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* --------------------------- Render --------------------------- */
  return (
    <div className="medication-form-container">
      <div className="form-header">
        <h2>
          <FaPills /> Agregar Nuevo Medicamento
        </h2>
        <p>Complete todos los campos requeridos para registrar un nuevo medicamento</p>
      </div>

      {/* Banner de éxito con Bootstrap + Bootstrap Icons */}
      {showSuccess && (
        <div
          className="alert alert-success d-flex align-items-center justify-content-between"
          role="alert"
        >
          <div className="d-flex align-items-center">
            <i className="bi bi-check-circle-fill me-2"></i>
            <div>
              <strong>Medicamento agregado correctamente.</strong> ¿Deseas agregar otro?
            </div>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-success btn-sm"
              onClick={() => setShowSuccess(false)}
            >
              Agregar otro
            </button>
            <button
              type="button"
              className="btn btn-outline-success btn-sm"
              onClick={() => {
                setShowSuccess(false);
                navigate("/");
              }}
            >
              Ir al inicio
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="medication-form">
        {/* Nombre del Medicamento */}
        <div className={`form-group ${errors.name ? "has-error" : ""}`}>
          <label htmlFor="name">
            <FaPills /> Nombre del Medicamento
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Ej: Paracetamol 500mg"
            autoComplete="off"
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        {/* Dosificación */}
        <div className={`form-group ${errors.dosage ? "has-error" : ""}`}>
          <label htmlFor="dosage">
            <FaPills /> Dosificación
          </label>
          <input
            type="text"
            id="dosage"
            name="dosage"
            value={formData.dosage}
            onChange={handleChange}
            placeholder="Ej: 500mg cada 8 horas"
            autoComplete="off"
          />
          {errors.dosage && <span className="error-message">{errors.dosage}</span>}
        </div>

        <div className="form-row">
          {/* Fecha de Vencimiento */}
          <div className={`form-group ${errors.expirationDate ? "has-error" : ""}`}>
            <label htmlFor="expirationDate">
              <FaCalendarAlt /> Fecha de Vencimiento
            </label>
            <input
              type="date"
              id="expirationDate"
              name="expirationDate"
              value={formData.expirationDate}
              onChange={handleChange}
              min={todayStr()}
            />
            {errors.expirationDate && (
              <span className="error-message">{errors.expirationDate}</span>
            )}
          </div>

          {/* Cantidad */}
          <div className={`form-group ${errors.quantity ? "has-error" : ""}`}>
            <label htmlFor="quantity">
              <FaBoxOpen /> Cantidad Disponible
            </label>
            <input
              type="number"
              id="quantity"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              min="1"
              placeholder="Ej: 10"
              inputMode="numeric"
            />
            {errors.quantity && (
              <span className="error-message">{errors.quantity}</span>
            )}
          </div>
        </div>

        <div className="form-row">
          {/* Condición */}
          <div className={`form-group ${errors.condition ? "has-error" : ""}`}>
            <label htmlFor="condition">Condición del Medicamento</label>
            <select
              id="condition"
              name="condition"
              value={formData.condition}
              onChange={handleChange}
            >
              <option value="">Seleccione una condición</option>
              {conditions.map((cond) => (
                <option key={cond} value={cond}>
                  {cond}
                </option>
              ))}
            </select>
            {errors.condition && (
              <span className="error-message">{errors.condition}</span>
            )}
          </div>

          {/* Categoría */}
          <div className={`form-group ${errors.category ? "has-error" : ""}`}>
            <label htmlFor="category">Categoría</label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleChange}
            >
              <option value="">Seleccione una categoría</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
            {errors.category && (
              <span className="error-message">{errors.category}</span>
            )}
          </div>
        </div>

        {/* Imagen del Medicamento */}
        <div className="form-group">
          <label>Imagen del Medicamento</label>
          <div className="image-upload-container">
            {formData.imagePreview ? (
              <div className="image-preview-container">
                <img
                  src={formData.imagePreview}
                  alt="Vista previa del medicamento"
                  className="image-preview"
                />
                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={removeImage}
                >
                  <FaTrash /> Eliminar
                </button>
              </div>
            ) : cameraActive ? (
              <div className="camera-container">
                <video ref={videoRef} autoPlay playsInline className="camera-feed" />
                <div className="camera-buttons">
                  <button type="button" className="btn-capture" onClick={takePhoto}>
                    <FaCamera /> Tomar Foto
                  </button>
                  <button type="button" className="btn-cancel" onClick={stopCamera}>
                    Cancelar
                  </button>
                </div>
                <canvas ref={canvasRef} style={{ display: "none" }} />
              </div>
            ) : (
              <div className="upload-options">
                <button
                  type="button"
                  className="btn-upload"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FaUpload /> Subir desde dispositivo
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  style={{ display: "none" }}
                />
                <button type="button" className="btn-camera" onClick={startCamera}>
                  <FaCamera /> Usar cámara
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Descripción Adicional */}
        <div className="form-group">
          <label htmlFor="description">
            <FaInfoCircle /> Descripción Adicional {" "}
            <span className="recommended-badge">Recomendado</span>
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Ej: Medicamento en su caja original con prospecto. Conservado en lugar fresco y seco."
            rows={3}
          />
          <p className="description-hint">
            Por favor incluya detalles como: estado del empaque, instrucciones especiales de almacenamiento, etc.
          </p>
        </div>

        <button type="submit" className="submit-btn" disabled={isSubmitting}>
          {isSubmitting ? (
            <span>Procesando...</span>
          ) : (
            <>
              <FaPlus /> Agregar Medicamento
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default AddMedication;
